import { useEffect } from 'react';
import { useGetJob } from '@/jobs/application/adapters/getJob';
import { useGetJobFormOptions } from '@/jobs/application/adapters/getJobFormOptions';
import { useSnackbar } from '@/common/presentation/SnackbarProvider';

export function useJobFormData(id?: number, skipQuery?: boolean) {
  const isNew = !id;
  const { showSnackbar } = useSnackbar();
  const jobId = Number(id);

  // Fetch existing job data when editing (only when we have a valid job ID)
  const {
    rawJob,
    job,
    loading: jobLoading,
    error: jobError,
    refetch: refetchJob,

    availableCrewMembers,
    availableCrewRoles,
    availableCustomers,
    availableDispatchLocations,
    availableDispatchers,
    availableEquipment,
    availableEquipmentLocations,
    availableEquipmentSizes,
    availableEquipmentTypes,
    availableExtraSystems,
    availableJobInformation,
    availableJobStatuses,
    availableJobTypes
  } = useGetJob(isNew ? undefined : jobId, skipQuery);

  // Fetch form options when creating a new job
  const {
    loading: formOptionsLoading,
    error: formOptionsError,

    availableCrewMembers: formOptionsCrewMembers,
    availableCrewRoles: formOptionsCrewRoles,
    availableCustomers: formOptionsCustomers,
    availableDispatchLocations: formOptionsDispatchLocations,
    availableDispatchers: formOptionsDispatchers,
    availableEquipment: formOptionsEquipment,
    availableEquipmentLocations: formOptionsEquipmentLocations,
    availableEquipmentSizes: formOptionsEquipmentSizes,
    availableEquipmentTypes: formOptionsEquipmentTypes,
    availableExtraSystems: formOptionsExtraSystems,
    availableJobInformation: formOptionsJobInformation,
    availableJobStatuses: formOptionsJobStatuses,
    availableJobTypes: formOptionsJobTypes,
    availableSalesAgents: formOptionsSalesAgents,
    availableSpecialFlags: formOptionsSpecialFlags,
    availableVendors: formOptionsVendors
  } = useGetJobFormOptions(!isNew || (skipQuery ?? false));

  useEffect(() => {
    if (jobError) {
      showSnackbar('Error', (jobError as { message: string })?.message || 'Failed to fetch job data', 'error');
    }
    if (formOptionsError) {
      showSnackbar('Error', (formOptionsError as { message: string })?.message || 'Failed to fetch form options', 'error');
    }
  }, [jobError, formOptionsError, showSnackbar]);

  return {
    isNew,

    rawJob,
    job,

    jobLoading,
    jobError,
    refetchJob,

    availableCrewMembers,
    availableCrewRoles,
    availableCustomers,
    availableDispatchLocations,
    availableDispatchers,
    availableEquipment,
    availableEquipmentLocations,
    availableEquipmentSizes,
    availableEquipmentTypes,
    availableExtraSystems,
    availableJobInformation,
    availableJobStatuses,
    availableJobTypes,

    formOptionsLoading,
    formOptionsError,

    formOptionsCrewMembers,
    formOptionsCrewRoles,
    formOptionsCustomers,
    formOptionsDispatchLocations,
    formOptionsDispatchers,
    formOptionsEquipment,
    formOptionsEquipmentLocations,
    formOptionsEquipmentSizes,
    formOptionsEquipmentTypes,
    formOptionsExtraSystems,
    formOptionsJobInformation,
    formOptionsJobStatuses,
    formOptionsJobTypes,
    formOptionsSalesAgents,
    formOptionsSpecialFlags,
    formOptionsVendors
  };
}
